import { useMutation, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { AccreditationStatusEnumType } from '__generated__/graphql';
import useParentRoute from 'hooks/useParentRoute';
import { FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import AccreditationProcessInfo from 'components/AccreditationProcessInfo';
import ErrorMessage from 'components/ErrorMessage';
import { FormInput, FormPanel } from 'components/FormPanel';

import Alert from 'primitives/Alert';
import LoadingIndicator from 'primitives/LoadingIndicator';

const SUBMIT_INVESTMENT_ENTITY_FOR_REVIEW_MUTATION = gql(`
  mutation SubmitInvestmentEntityForReview($investmentEntityId: ID!) {
    submitInvestmentEntityForReview(id: $investmentEntityId) {
      id
    }
  }
`);

const INVESTOR_APPLICATION_ACCREDITATION_STATUS_QUERY = gql(`
  query InvestorApplicationAccreditationStatus($id: ID!) {
    investmentEntity(id: $id) {
      id
      investorApplication {
        id
        accreditationStatus
      }
    }
  }
`);

const SubmitInvestmentEntityForReview: FC = () => {
  const navigate = useNavigate();
  const parentRoute = useParentRoute();
  const { investmentEntityId } = useParams<{ investmentEntityId: string }>() as {
    investmentEntityId: string;
  };

  const { loading, error, data, refetch } = useQuery(
    INVESTOR_APPLICATION_ACCREDITATION_STATUS_QUERY,
    {
      variables: {
        id: investmentEntityId,
      },
    }
  );

  const [submitInvestmentEntityForReview, { loading: signLoading, error: signError }] = useMutation(
    SUBMIT_INVESTMENT_ENTITY_FOR_REVIEW_MUTATION
  );

  if (loading) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const investorApplicationAccreditationStatus =
    data.investmentEntity.investorApplication?.accreditationStatus;

  return (
    <FormPanel
      loading={signLoading || loading}
      error={signError || error}
      onSubmit={_data => {
        submitInvestmentEntityForReview({
          variables: {
            investmentEntityId,
          },
        }).then(() => {
          if (
            investorApplicationAccreditationStatus === AccreditationStatusEnumType.InitiateProcess
          )
            window.open('https://accreditation.navigateaif.co.in/#/sign-up', '_blank');

          navigate(`/invest/investment-entities`);
        });
      }}
      onCancel={() => {
        navigate(`${parentRoute}/investor-application`);
      }}
      submitButtonLabel="Submit"
      cancelButtonLabel="Back"
    >
      <FormInput
        type="switch"
        fieldName="disclaimer"
        label="I confirm that the information provided is accurate to the best of my knowledge"
        defaultValue=""
        validators={{
          required: true,
        }}
      />
      <AccreditationProcessInfo
        className={
          investorApplicationAccreditationStatus === AccreditationStatusEnumType.InitiateProcess
            ? 'block'
            : 'hidden'
        }
      />
      <Alert
        type="info"
        title=""
        message="Please note that SEBI has mandated all AIFs to issue units in dematerialised form only. Accordingly, the investor must hold an active demat account at the time of making investments. Furthermore, the bank account and the demat account must be registered in the same name as that of the investor (i.e., the investing entity)."
      />
    </FormPanel>
  );
};

export default SubmitInvestmentEntityForReview;
